import type { DropResult } from '@hello-pangea/dnd'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import type { FeedbackRTValue } from '@vedalib/editor/lib/elements'
import { TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import { getFeedbackValue } from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'

import type { TestRankingElementType } from '../testRankingTypes'
import * as s from './TestRankingElement.module.scss'
import TestRankingElementEditorItem from './TestRankingElementEditorItem'

const TestRankingElementEditor: TestRankingElementType = ({
  element,
  mode,
  styles,
  waiting,
  font,
  onChange,
  state,
  feedback,
}) => {
  const { id: elementId, value } = element
  const { items } = value
  const rootStyles = useElementRootStyles(styles.root)
  const { generalFbValid } = state || {}
  const feedbackValue = getFeedbackValue(value)

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })

  const handleChangeGeneralFeedback = (newFeedbackValue: FeedbackRTValue) => {
    onChange?.(R.assoc('generalFeedback', newFeedbackValue, element.value))
  }

  const handleMove = ({ destination, source }: DropResult) =>
    destination &&
    onChange?.(R.assoc('items', R.move(source.index, destination.index, items), value))

  return (
    <div style={rootStyles}>
      <DragDropContext onDragEnd={handleMove}>
        <Droppable
          droppableId='droppable'
          renderClone={(provided, _snapshot, { source }) => (
            <TestRankingElementEditorItem
              element={element}
              feedback={feedback}
              font={font}
              index={source.index}
              mode={mode}
              onChange={onChange}
              provided={provided}
              styles={styles}
              waiting={waiting}
              isDragging
            />
          )}
        >
          {(provided, { isDraggingOver }) => (
            <div className={s.items} ref={provided.innerRef} {...provided.droppableProps}>
              {items.map((_item, index) => (
                <Draggable draggableId={index.toString()} index={index} key={index}>
                  {(provided, snapshot) => (
                    <TestRankingElementEditorItem
                      element={element}
                      feedback={feedback}
                      font={font}
                      index={index}
                      isDragging={snapshot.isDragging}
                      mode={mode}
                      onChange={onChange}
                      provided={provided}
                      showControls={!isDraggingOver}
                      styles={styles}
                      waiting={waiting}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <TestFeedback
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={generalFbValid}
        mode={mode}
        onChange={handleChangeGeneralFeedback}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: value.generalFeedback,
        }}
        showFeedback={
          value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
        }
        styles={feedback}
      />
    </div>
  )
}

export default TestRankingElementEditor
